import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Eye, CheckCircle2, XCircle, FileSpreadsheet, Upload } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  draft: { label: "DRAFT", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  posted: { label: "POSTED", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelled: { label: "CANCELLED", cls: "bg-slate-200 text-slate-600 border-slate-300" },
};

export default function OpeningBalance() {
  const [tab, setTab] = useState("warehouse");
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesList, setSalesList] = useState([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [obDate, setObDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [obSales, setObSales] = useState("");
  const [obNotes, setObNotes] = useState("");
  const [items, setItems] = useState([{ _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }]);

  const [importRows, setImportRows] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importTotal, setImportTotal] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/opening-balances"), api.get("/products"), api.get("/sales-agents")]);
    setRows(a.data); setProducts(b.data); setSalesList(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => r.location_type === tab);

  const resetForm = () => {
    setEditing(null); setObNotes(""); setObSales("");
    setObDate(new Date().toISOString().slice(0, 10));
    setItems([{ _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }]);
  };

  const setItem = (i, k, v) => {
    const n = [...items];
    n[i] = { ...n[i], [k]: v };
    if (k === "product_id") {
      const p = products.find(p => p.product_id === v);
      if (p) { n[i].cost_price = p.cost_price; n[i].unit = p.unit; }
    }
    setItems(n);
  };

  const totalValue = items.reduce((a, it) => {
    const p = products.find(p => p.product_id === it.product_id);
    const factor = p?.packs?.find(pk => pk.unit === it.unit)?.factor || 1;
    return a + Number(it.qty || 0) * factor * Number(it.cost_price || 0);
  }, 0);

  const save = async () => {
    try {
      const payload = { location_type: tab, date: obDate, sales_id: tab === "sales" ? obSales : null, notes: obNotes, items: items.filter(i => i.product_id && i.qty > 0) };
      if (editing) {
        await api.put(`/opening-balances/${editing.ob_id}`, payload);
        toast.success("Saldo awal diperbarui");
      } else {
        await api.post("/opening-balances", payload);
        toast.success("Saldo awal tersimpan sebagai DRAFT — klik Posting untuk memasukkan ke stok");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setObDate(r.date); setObSales(r.sales_id || ""); setObNotes(r.notes || "");
    setItems(r.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, cost_price: it.cost_price, unit: it.unit })));
    setOpen(true);
  };

  const post = async (r) => {
    if (!window.confirm(`Posting ${r.ob_no}? Stok akan bertambah sesuai saldo awal.`)) return;
    try { await api.post(`/opening-balances/${r.ob_id}/post`); toast.success(`${r.ob_no} diposting ke stok`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal posting"); }
  };

  const cancel = async (r) => {
    const reason = window.prompt(`Batalkan ${r.ob_no}? Stok akan direversal (-). Masukkan alasan:`);
    if (reason === null) return;
    try { await api.post(`/opening-balances/${r.ob_id}/cancel`, { reason }); toast.success(`${r.ob_no} dibatalkan — stok direversal`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membatalkan"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus draft ${r.ob_no}?`)) return;
    try { await api.delete(`/opening-balances/${r.ob_id}`); toast.success("Draft dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const doImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/opening-balances/import/preview?location_type=${tab}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportRows(r.data.rows); setImportErrors(r.data.errors); setImportTotal(r.data.total);
      if (r.data.error_count === 0) toast.success(`${r.data.valid} baris valid — siap diimport`);
      else toast.warning(`${r.data.error_count} baris error — perbaiki dulu`);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membaca file"); }
  };

  const commitImport = async () => {
    try {
      const r = await api.post("/opening-balances/import/commit", { location_type: tab, date: obDate, rows: importRows });
      toast.success(`${r.data.created.length} saldo awal dibuat sebagai DRAFT`);
      setImportRows(null); setImportErrors([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal import"); }
  };

  return (
    <div data-testid="page-opening" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Penggunaan Awal</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Saldo Awal</h1>
        <p className="text-slate-500 text-sm mt-1">Posisi stok sebenarnya sebelum aplikasi dipakai. Bukan pembelian/penjualan — tercatat sebagai movement OPENING_BALANCE dan dapat diaudit.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="warehouse" data-testid="tab-ob-warehouse">Saldo Awal Gudang</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-ob-armada">Saldo Awal Armada</TabsTrigger>
        </TabsList>

        <div className="flex gap-2 justify-end mt-4 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/opening-balances/template?kind=${tab === "sales" ? "armada" : "warehouse"}`, "template_saldo_awal.xlsx")} data-testid="btn-ob-template"><FileSpreadsheet className="h-4 w-4 mr-1" />Template Excel</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-ob-import"><Upload className="h-4 w-4 mr-1" />Import Excel</Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={doImportFile} data-testid="input-ob-file" />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-ob" onClick={resetForm}><Plus className="h-4 w-4 mr-1" />Saldo Awal {tab === "sales" ? "Armada" : "Gudang"}</Button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? `Ubah ${editing.ob_no}` : `Saldo Awal ${tab === "sales" ? "Armada" : "Gudang"}`}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tanggal Saldo Awal</Label><Input type="date" value={obDate} onChange={(e) => setObDate(e.target.value)} disabled={!!editing} data-testid="input-ob-date" /></div>
                {tab === "sales" ? (
                  <div><Label>Sales / Armada</Label>
                    <Select value={obSales} onValueChange={setObSales} disabled={!!editing}>
                      <SelectTrigger data-testid="input-ob-sales"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
                      <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.code} · {s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : <div><Label>Gudang</Label><Input value="Gudang Utama" disabled /></div>}
                <div className="col-span-2"><Label>Keterangan</Label><Input value={obNotes} onChange={(e) => setObNotes(e.target.value)} placeholder="cth: Stok per 31 Agustus 2026" /></div>
              </div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mt-1 mb-1">Detail Barang</div>
              {items.map((it, i) => (
                <div key={it._k} className="grid grid-cols-[1fr_70px_80px_120px_36px] gap-2 items-center mb-2">
                  <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                    <SelectTrigger data-testid={`ob-${i}-product`}><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                    <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`ob-${i}-qty`} />
                  <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => setItem(i, "unit", v)} testid={`ob-${i}-unit`} />
                  <Input type="number" placeholder="Harga modal" value={it.cost_price} onChange={(e) => setItem(i, "cost_price", e.target.value)} data-testid={`ob-${i}-cost`} />
                  <Button variant="ghost" size="icon" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setItems([...items, { _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }])} data-testid="btn-ob-add-row"><Plus className="h-3 w-3 mr-1" />Baris</Button>
              <DialogFooter className="items-center">
                <div className="mr-auto text-sm">Total nilai: <span className="font-mono font-bold">{rupiah(totalValue)}</span></div>
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-ob">{editing ? "Simpan Perubahan" : "Simpan Draft"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <TabsContent value={tab}>
          <Card>
            <table className="w-full text-sm" data-testid="table-ob">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">Nomor</th><th>Tanggal</th><th>Lokasi/Sales</th><th className="text-right">Item</th><th className="text-right">Total Nilai</th><th>Status</th><th>Dibuat Oleh</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.ob_id} className="border-b">
                    <td className="py-2 px-4 font-mono text-xs">{r.ob_no}</td>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.location_type === "warehouse" ? "Gudang Utama" : `${r.sales_name}${r.vehicle_no ? ` · ${r.vehicle_no}` : ""}`}</td>
                    <td className="text-right font-mono">{r.items.length}</td>
                    <td className="text-right font-mono">{rupiah(r.total_value)}</td>
                    <td><Badge className={`${STATUS_META[r.status].cls} font-normal`} data-testid={`ob-status-${r.ob_no}`}>{STATUS_META[r.status].label}</Badge></td>
                    <td className="text-xs text-slate-500">{r.created_by}</td>
                    <td className="whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewing(r)} data-testid={`view-${r.ob_no}`}><Eye className="h-3.5 w-3.5" /></Button>
                      {r.status === "draft" && <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-ob-${r.ob_no}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => post(r)} data-testid={`post-${r.ob_no}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Posting</Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(r)} data-testid={`delete-ob-${r.ob_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                      </>}
                      {r.status === "posted" && <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700" onClick={() => cancel(r)} data-testid={`cancel-${r.ob_no}`}><XCircle className="h-3.5 w-3.5 mr-1" />Cancel</Button>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Belum ada saldo awal {tab === "sales" ? "armada" : "gudang"}</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{viewing?.ob_no} <Badge className={`ml-2 ${viewing ? STATUS_META[viewing.status].cls : ""} font-normal`}>{viewing && STATUS_META[viewing.status].label}</Badge></DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Tanggal:</span> <span className="font-mono">{formatDate(viewing.date)}</span></div>
                <div><span className="text-slate-500">Lokasi:</span> {viewing.location_type === "warehouse" ? "Gudang Utama" : viewing.sales_name}</div>
                <div><span className="text-slate-500">Dibuat:</span> {viewing.created_by}</div>
                {viewing.posted_by && <div><span className="text-slate-500">Diposting:</span> {viewing.posted_by}</div>}
                {viewing.cancelled_by && <div className="col-span-2"><span className="text-slate-500">Dibatalkan:</span> {viewing.cancelled_by} — {viewing.cancel_reason}</div>}
                {viewing.notes && <div className="col-span-2"><span className="text-slate-500">Ket:</span> {viewing.notes}</div>}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs font-mono uppercase text-slate-500 border-b text-left"><tr><th className="py-1">SKU</th><th>Barang</th><th className="text-right">Jumlah</th><th className="text-right">Harga Modal</th><th className="text-right">Nilai</th></tr></thead>
                <tbody>
                  {viewing.items.map(it => (
                    <tr key={it.product_id} className="border-b"><td className="py-1.5 font-mono text-xs">{it.sku}</td><td>{it.name}</td><td className="text-right font-mono">{num(it.qty)} {it.unit}</td><td className="text-right font-mono">{rupiah(it.cost_price)}</td><td className="text-right font-mono">{rupiah(it.value)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr className="font-bold"><td colSpan={4} className="py-2 text-right">Total</td><td className="text-right font-mono">{rupiah(viewing.total_value)}</td></tr></tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import preview dialog */}
      <Dialog open={!!importRows} onOpenChange={(o) => !o && setImportRows(null)}>
        <DialogContent className="max-w-2xl" data-testid="ob-import-preview">
          <DialogHeader><DialogTitle>Preview Import Saldo Awal</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="border rounded p-3"><div className="text-xs font-mono uppercase text-slate-500">Total Baris</div><div className="font-mono font-bold text-xl">{importTotal}</div></div>
            <div className="border rounded p-3"><div className="text-xs font-mono uppercase text-slate-500">Valid</div><div className="font-mono font-bold text-xl text-emerald-700">{importRows?.length || 0}</div></div>
            <div className="border rounded p-3"><div className="text-xs font-mono uppercase text-slate-500">Error</div><div className="font-mono font-bold text-xl text-rose-700">{importErrors.length}</div></div>
          </div>
          {importErrors.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm max-h-48 overflow-y-auto" data-testid="ob-import-errors">
              {importErrors.map((e) => <div key={e.row} className="text-rose-800 font-mono text-xs py-0.5">Baris {e.row}: {e.message}</div>)}
            </div>
          )}
          <p className="text-xs text-slate-500">Hasil import dibuat sebagai DRAFT — periksa lalu klik Posting untuk memasukkan ke stok. Import diblokir jika masih ada baris error.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportRows(null)}>Tutup</Button>
            <Button onClick={commitImport} disabled={importErrors.length > 0} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-ob-commit">Import {importRows?.length || 0} Baris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
